import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { Header, Text, VStack } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import {
  LayoutRectangle,
  NativeSyntheticEvent,
  Platform,
  TextInput as RNTextInput,
  TextInputChangeEventData,
} from 'react-native';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CircularWarningIcon } from 'components/icons/circular-warning-icon';
import { toaster } from 'components/toaster/toaster';
import { TextInput } from 'components/ucl/text-input';
import { GraphQLErrorMessages } from 'enums/graphql';
import { ProviderType } from 'generated/graphql-gateway';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import useErrorModal, { IErrorModal } from 'hooks/use-error-modal';
import AppleAuthButton from 'pages/authentication/components/social-auth-buttons/apple/apple-auth-button';
import GoogleAuthButton from 'pages/authentication/components/social-auth-buttons/google/google-auth-button';
import { UserAuth } from 'pages/authentication/components/social-auth-buttons/types';
import { EmailIcon } from 'pages/authentication/sign-in/EmailIcon';
import { HttpErrorCodes } from 'remote/constants';
import { logEvent, logSignInEvent, logSignUpEvent, logSocialSignInError } from 'state/amplitude';
import {
  AppleError,
  AuthenticationMethod,
  AuthenticationPhases,
  CustomEventNames,
  EventTypes,
} from 'state/amplitude/constants';
import { useAuthContext } from 'state/auth';
import {
  SocialLoginError,
  USER_DECLINED_EMAIL_ACCESS_PERMISSION_ERROR_MESSAGE,
} from 'state/auth/errors';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { IOTPSignInParams, ModalAuthScreen } from 'state/auth/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useScrollContext } from 'state/scroll';
import { useUIContext } from 'state/ui';
import { isIOS, isWeb } from 'utils/environment';
import { GraphQLErrorCodes, parseGraphQLErrorCodes } from 'utils/errors';
import { isEmailValid } from 'utils/form';
import LocalStorage from 'utils/local-storage';
import { Keys } from 'utils/local-storage/constants';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { IAuthCampaign } from '../components/auth-campaigns/hooks/use-auth-campaign';

import { LabelStyled, StyledButtonWidthLimit, TextInputWrapper } from './styled';

// Additional Y amount for show email input and submit button
const ADDITIONAL_SCROLL_Y = 50;

const MAX_SOCIAL_LOGIN_ERROR_DIALOG_CONTENT_WIDTH = '520px';

interface ISignInFormProps {
  authCampaignExtraFields?: IAuthCampaign['extraFields'];
}

const PROVIDER_TYPE_DISPLAY_VALUE_MAP = {
  [ProviderType.APPLE]: 'Apple',
  [ProviderType.GOOGLE]: 'Google',
};

export const SignInForm = ({ authCampaignExtraFields }: ISignInFormProps) => {
  const {
    isAuthenticated,
    signIn,
    socialLogin,
    setModalAuthState,
    modalAuthIsOpen,
    originLocation,
    modalAuthState,
    user,
    setIsReAuthenticating,
    setOriginLocation,
    onSignInSuccess,
    alertMessage,
  } = useAuthContext();

  const { formatMessage } = useIntl();
  const { errorMessage: initialErrorMessage } = useAuthFlow();

  const { userEmail } = useUIContext();
  const { signUpEmail } = useLocationContext();

  const emailInputEl = useRef<RNTextInput>(null);
  const isGoogleAuthEnabled = useFlag(LaunchDarklyFlag.ENABLE_SOCIAL_LOGIN_GOOGLE);
  const isAppleAuthEnabled = useFlag(LaunchDarklyFlag.ENABLE_SOCIAL_LOGIN_APPLE);
  const isOTPFlowEnabled = useFlag(LaunchDarklyFlag.ENABLE_OTP_LOGIN);
  const router = useRouter();

  const defaultEmail = signUpEmail || userEmail || '';
  const [emailInput, setEmailInput] = useState(defaultEmail);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    modalAuthState?.errorMessage || initialErrorMessage
  );
  const [shouldDisableAuthButtons, setShouldDisableAuthButtons] = useState(false);
  const disableEmailInput = !!modalAuthState.disableEmailInput;

  useLogUserErrorMessage({ message: errorMessage, severity: LogUserErrorSeverity.Recoverable });

  const [ErrorDialog, openErrDialog, dismissDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Sign In Failure',
  });

  const [EmailDoesNotMatchErrorDialog, openEmailDoesNotMatchErrDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Social login attempt with different email',

    onConfirm: () => {
      // This causes the useAuthRedirects() hook to navigate away from the account
      // if the user re-authenticates via social login and chooses
      // an account with a different email.
      setIsReAuthenticating(false);
    },
  });

  const attemptSignIn = useCallback(
    async ({ email = '' }: IOTPSignInParams) => {
      try {
        setIsLoading(true);
        setShouldDisableAuthButtons(true);

        if (modalAuthState.onPreSignIn) {
          await modalAuthState.onPreSignIn();
        }

        await signIn({
          email,
        });
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        setShouldDisableAuthButtons(false);

        let message = formatMessage({ id: 'authError' });

        if (error?.graphQLErrors?.[0]?.extensions?.statusCode === HttpErrorCodes.TooManyRequests) {
          message = `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
            id: 'pleaseTryAgainLater',
          })}`;
        }

        openErrDialog({
          error,
          message,
        });

        logger.warn({ error });
      }
    },
    [modalAuthState, signIn, formatMessage, openErrDialog]
  );

  const getSocialLoginUserFriendlyMessageForError = (error: SocialLoginError): string => {
    switch (error.message) {
      case USER_DECLINED_EMAIL_ACCESS_PERMISSION_ERROR_MESSAGE:
        return formatMessage(
          { id: 'userSocialLoginEmailUnavailable' },
          { providerType: PROVIDER_TYPE_DISPLAY_VALUE_MAP[error.providerType] }
        );
      default:
        return formatMessage({ id: 'userSocialLoginGenericErrorDescription' });
    }
  };

  const getSocialLoginErrorDialogProps = ({
    title,
    message,
  }: {
    title: string;
    message: string;
  }): Pick<IErrorModal, 'title' | 'message' | 'dialogProps'> => {
    return {
      dialogProps: {
        contentMaxWidth: MAX_SOCIAL_LOGIN_ERROR_DIALOG_CONTENT_WIDTH,
        headingComponent: (
          <Header variant={'headerTwo'} alignSelf={'center'} _android={{ paddingTop: '$14' }}>
            <VStack alignItems={'center'}>
              <CircularWarningIcon />
              <Text marginTop="$3">{title}</Text>
            </VStack>
          </Header>
        ),
      },
      message: <Text textAlign="center">{message}</Text>,
    };
  };

  // Set the focus on Email input text when the modal is open
  useEffect(() => {
    if (showForm && isWeb) {
      setFocusOnEmailInput();
    }
  }, [showForm]);

  const labelMessage = formatMessage({ id: 'emailAddress' });
  const requiredErrorMessage = formatMessage({ id: 'emailRequiredError' });
  const invalidErrorMessage = formatMessage({ id: 'notValidEmailError' });

  const handleEmailChange = (ev: NativeSyntheticEvent<TextInputChangeEventData>) => {
    setErrorMessage(undefined);
    setEmailInput(ev.nativeEvent.text);
  };

  const setFocusOnEmailInput = () => {
    if (emailInputEl.current) {
      emailInputEl.current.focus();
    }
  };

  const parse = useCallback(
    async (input: string) => {
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        setErrorMessage(requiredErrorMessage);
        setFocusOnEmailInput();
        return {};
      }

      const noLetters = /^[\0-9]{2,}$/.test(trimmedInput);

      const isEmail = isEmailValid(trimmedInput);

      if (!noLetters && !isEmail) {
        setErrorMessage(formatMessage({ id: 'notValidEmailError' }));
        setFocusOnEmailInput();
        return {};
      }

      if (isEmail) {
        return { email: trimmedInput };
      }

      setErrorMessage(invalidErrorMessage);
      // If there was an error, try to focus the input after an attempted sign in
      // this will force screen readers to read the alert even if it never left the screen
      setFocusOnEmailInput();
      return {};
    },
    [invalidErrorMessage, requiredErrorMessage, formatMessage]
  );

  const handleSubmit = useCallback(async () => {
    const { email } = await parse(emailInput);

    if (email) {
      setErrorMessage('');
      attemptSignIn({ email });
    }
  }, [attemptSignIn, emailInput, parse]);

  const { scrollTo } = useScrollContext();

  const emailInputPosition = useRef<LayoutRectangle>(undefined);

  const handleEmailFocus = useCallback(() => {
    if (emailInputPosition.current && Platform.OS === 'android') {
      scrollTo({
        x: 0,
        y: emailInputPosition.current.y + emailInputPosition.current.height + ADDITIONAL_SCROLL_Y,
      });
    }
  }, [scrollTo]);

  const socialSignIn = useCallback(
    async (userInfo: UserAuth, providerType: ProviderType) => {
      try {
        if (modalAuthState.onPreSignIn) {
          await modalAuthState.onPreSignIn();
        }

        if (userInfo.email) {
          if (isAuthenticated && user?.details.email !== userInfo.email) {
            setIsLoadingApple(false);
            setIsLoadingGoogle(false);
            setShouldDisableAuthButtons(false);

            openEmailDoesNotMatchErrDialog({
              message: formatMessage({ id: 'emailAddressDoesNotMatch' }),
            });

            logSocialSignInError(formatMessage({ id: 'emailAddressDoesNotMatch' }), providerType);

            return;
          }

          if (LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_EMAIL) !== userInfo.email) {
            LocalStorage.setItem(Keys.SOCIAL_LOGIN_USER_EMAIL, userInfo.email);
          }
        } else {
          const message = formatMessage(
            { id: 'userSocialLoginEmailUnavailable' },
            { providerType: PROVIDER_TYPE_DISPLAY_VALUE_MAP[providerType] }
          );

          openErrDialog({
            ...getSocialLoginErrorDialogProps({
              title: formatMessage({ id: 'userSocialLoginFailed' }),
              message,
            }),
          });

          logSocialSignInError(message, providerType);
          setShouldDisableAuthButtons(false);

          return;
        }

        if (
          userInfo?.name?.trim()?.length &&
          LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_NAME) !== userInfo.name
        ) {
          LocalStorage.setItem(Keys.SOCIAL_LOGIN_USER_NAME, userInfo.name);
        }

        await socialLogin({
          providerType,
          providerToken: userInfo.token || '',
        });

        LocalStorage.removeItem(Keys.SOCIAL_LOGIN_USER_NAME);

        if (!modalAuthIsOpen) {
          const path = originLocation || routes.base;
          setOriginLocation(null);

          toaster.positive(formatMessage({ id: 'signInSuccess' }));
          return router.replace(path);
        }

        onSignInSuccess();
      } catch (error) {
        let message = formatMessage({ id: 'authError' });
        const title = formatMessage({ id: 'userSocialLoginFailed' });

        if (error instanceof ApolloError) {
          const gqlErrorCodes = parseGraphQLErrorCodes(error);

          const userNotFoundError = error.graphQLErrors.find(
            error => error.extensions.code === GraphQLErrorCodes.USER_NOT_FOUND
          );

          const isUserNotFoundError =
            !!userNotFoundError || error.message === GraphQLErrorMessages.USER_NOT_FOUND;

          const isEmailNotVerifiedError = gqlErrorCodes.some(
            parsedError => parsedError.errorCode === GraphQLErrorCodes.EMAIL_NOT_VERIFIED
          );

          const isTooManyRequestsError =
            error.graphQLErrors?.[0]?.extensions?.statusCode === HttpErrorCodes.TooManyRequests;

          const isEmailMissingFromTokenError = gqlErrorCodes.some(
            parsedError => parsedError.errorCode === GraphQLErrorCodes.EMAIL_MISSING_FROM_TOKEN
          );

          if (isUserNotFoundError) {
            const notFoundUserData: unknown = userNotFoundError?.extensions.user;

            let fallbackUserName: string | undefined = undefined;
            let fallbackUserEmail: string | undefined = undefined;

            if (notFoundUserData && typeof notFoundUserData === 'object') {
              if (
                'name' in notFoundUserData &&
                typeof notFoundUserData.name === 'string' &&
                notFoundUserData.name
              ) {
                fallbackUserName = notFoundUserData.name;
              }

              if (
                'email' in notFoundUserData &&
                typeof notFoundUserData.email === 'string' &&
                notFoundUserData.email
              ) {
                fallbackUserEmail = notFoundUserData.email;
              }
            }

            const email = LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_EMAIL) || fallbackUserEmail;
            const name = LocalStorage.getItem(Keys.SOCIAL_LOGIN_USER_NAME) || fallbackUserName;

            logSocialSignInError(error?.message || '', providerType);

            setModalAuthState({
              screen: ModalAuthScreen.SIGN_UP,
              shouldDisplaySignInSuccessMessage: false,
              user: { name, email },
              providerType,
            });

            logSignUpEvent({
              authenticationMethod: AuthenticationMethod.SOCIAL,
              socialAuthenticationType: providerType,
              phase: AuthenticationPhases.START,
            });

            return;
          } else if (isEmailNotVerifiedError) {
            message = formatMessage({ id: 'userAuthEmailNotVerified' });
          } else if (isEmailMissingFromTokenError) {
            message = formatMessage(
              { id: 'userSocialLoginEmailUnavailable' },
              { providerType: PROVIDER_TYPE_DISPLAY_VALUE_MAP[providerType] }
            );
          } else if (isTooManyRequestsError) {
            message = `${formatMessage({ id: 'maxAttemptsReached' })} ${formatMessage({
              id: 'pleaseTryAgainLater',
            })}`;
          }
        }

        logSocialSignInError(message, providerType);

        openErrDialog({
          error: error as Error,
          ...getSocialLoginErrorDialogProps({
            title,
            message,
          }),
          onConfirm: () => socialSignIn(userInfo, providerType),
          onDismiss: dismissDialog,
          confirmationText: formatMessage({ id: 'retry' }),
          dismissText: formatMessage({ id: 'close' }),
        });

        logger.warn({ error });
      }
    },
    [
      modalAuthState,
      socialLogin,
      modalAuthIsOpen,
      onSignInSuccess,
      isAuthenticated,
      user?.details.email,
      openEmailDoesNotMatchErrDialog,
      formatMessage,
      originLocation,
      setOriginLocation,
      router,
      openErrDialog,
      dismissDialog,
      setModalAuthState,
    ]
  );

  const signInUpForm = (
    <>
      <TextInputWrapper>
        <TextInput
          alertMessage={alertMessage}
          accessibilityRole="text"
          accessibilityLabel={errorMessage ? errorMessage : labelMessage}
          testID="signin-email-input"
          errorMessage={errorMessage}
          label={labelMessage}
          required
          type="email"
          onChange={handleEmailChange}
          ref={emailInputEl}
          autoComplete="email"
          value={emailInput}
          autoCapitalize="none"
          keyboardType="email-address"
          returnKeyType="done"
          onSubmitEditing={handleSubmit}
          onLayout={ev => {
            emailInputPosition.current = ev.nativeEvent.layout;
            if (isWeb) {
              // This is a hack to avoid overlapping on mobile web
              const element = document.querySelector('[data-testid="keyboard-aware-auth-modal"]');
              const signinEmailInput = document.querySelector('[data-testid="signin-email-input"]');
              if (element && !isIOS()) {
                element.scrollTop = element.scrollHeight + ADDITIONAL_SCROLL_Y;
              } else if (signinEmailInput) {
                scrollIntoView(signinEmailInput);
              }
            }
          }}
          onFocus={handleEmailFocus}
          placeholder={formatMessage({ id: 'emailAddress' })}
          isDisabled={!!disableEmailInput}
        />
      </TextInputWrapper>
      {authCampaignExtraFields}
      <StyledButtonWidthLimit>
        <ActionButton
          fullWidth
          testID="signin-button"
          isLoading={isLoading}
          disabled={shouldDisableAuthButtons}
          onPress={handleSubmit}
        >
          {formatMessage({ id: 'signInUp' })}
        </ActionButton>
      </StyledButtonWidthLimit>
    </>
  );

  const handleSocialLoginError = (
    error: any,
    providerType: ProviderType,
    setProviderLoading: (value: boolean) => void
  ) => {
    setProviderLoading(false);
    setShouldDisableAuthButtons(false);
    // Handle cancellation cases
    if (
      // Google cancel case
      (providerType === ProviderType.GOOGLE && error.code === '-5') ||
      // Apple cancel case
      (providerType === ProviderType.APPLE &&
        (error.code === AppleError.CANCELED || error.code === AppleError.UNKNOWN))
    ) {
      logSignInEvent({
        phase: AuthenticationPhases.CANCEL,
        authenticationMethod: AuthenticationMethod.SOCIAL,
        socialAuthenticationType: providerType,
        message: error?.message ?? '',
      });
      return;
    }

    logSocialSignInError(error?.message ?? '', providerType);

    // Skip error dialog for specific cases
    if (
      (providerType === ProviderType.GOOGLE &&
        (error.code === 'COOKIE_CLOSE_POPUP' ||
          error?.message?.includes('cancelled') ||
          error?.message?.includes('canceled the sign in request'))) ||
      (providerType === ProviderType.APPLE &&
        (error?.err?.error === 'popup_closed_by_user' ||
          error?.message?.includes('AuthorizationError')))
    ) {
      return;
    }

    // Show error dialog
    openErrDialog({
      error,
      ...getSocialLoginErrorDialogProps({
        title: formatMessage({ id: 'userSocialLoginFailed' }),
        message: getSocialLoginUserFriendlyMessageForError(error),
      }),
    });
  };

  const handleOnSuccessSocialLogin = async (
    userInfo: any,
    providerType: ProviderType,
    setProviderLoading: (value: boolean) => void
  ) => {
    try {
      const eventNamesSocialLoginSuccessMap: Record<
        ProviderType.GOOGLE | ProviderType.APPLE,
        CustomEventNames
      > = {
        [ProviderType.GOOGLE]: CustomEventNames.SOCIAL_LOGIN_GOOGLE_SUCCESS,
        [ProviderType.APPLE]: CustomEventNames.SOCIAL_LOGIN_APPLE_SUCCESS,
      };
      const eventName = eventNamesSocialLoginSuccessMap[providerType] || '';
      logEvent(eventName, EventTypes.Other);
      await socialSignIn(userInfo, providerType);
    } finally {
      setProviderLoading(false);
    }
  };

  const handleOnStartSocialLogin = (
    providerType: ProviderType,
    setProviderLoading: (value: boolean) => void
  ) => {
    const eventNamesSocialLoginClickMap: Record<
      ProviderType.GOOGLE | ProviderType.APPLE,
      CustomEventNames
    > = {
      [ProviderType.GOOGLE]: CustomEventNames.SOCIAL_LOGIN_GOOGLE_CLICK,
      [ProviderType.APPLE]: CustomEventNames.SOCIAL_LOGIN_APPLE_CLICK,
    };
    const eventName = eventNamesSocialLoginClickMap[providerType] || '';
    logEvent(eventName, EventTypes.Other);
    setProviderLoading(true);
    setShouldDisableAuthButtons(true);
    logSignInEvent({
      phase: AuthenticationPhases.START,
      authenticationMethod: AuthenticationMethod.SOCIAL,
      socialAuthenticationType: providerType,
    });
  };

  return (
    <>
      {isGoogleAuthEnabled && (
        <GoogleAuthButton
          isLoading={isLoadingGoogle}
          isDisabled={shouldDisableAuthButtons}
          onStart={() => handleOnStartSocialLogin(ProviderType.GOOGLE, setIsLoadingGoogle)}
          onSuccess={userInfo =>
            handleOnSuccessSocialLogin(userInfo, ProviderType.GOOGLE, setIsLoadingGoogle)
          }
          onError={error => handleSocialLoginError(error, ProviderType.GOOGLE, setIsLoadingGoogle)}
        />
      )}
      {isAppleAuthEnabled && (
        <AppleAuthButton
          isLoading={isLoadingApple}
          isDisabled={shouldDisableAuthButtons}
          onStart={() => handleOnStartSocialLogin(ProviderType.APPLE, setIsLoadingApple)}
          onSuccess={userInfo =>
            handleOnSuccessSocialLogin(userInfo, ProviderType.APPLE, setIsLoadingApple)
          }
          onError={error => handleSocialLoginError(error, ProviderType.APPLE, setIsLoadingApple)}
        />
      )}
      {!showForm && isOTPFlowEnabled && (
        <ActionButton
          eventAttributes={{ Name: 'Continue with Email' }}
          leftIcon={<EmailIcon />}
          fullWidth
          borderColor={Styles.color.black}
          marginY="$2"
          variant={ActionButtonVariants.OUTLINE}
          disabled={shouldDisableAuthButtons}
          onPress={() => setShowForm(true)}
        >
          <LabelStyled>{formatMessage({ id: 'continueWithEmail' })}</LabelStyled>
        </ActionButton>
      )}
      {isOTPFlowEnabled && showForm && signInUpForm}
      <ErrorDialog />
      <EmailDoesNotMatchErrorDialog />
    </>
  );
};
